import React, { useState, useMemo } from 'react'
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem, Input, Icon, IconButton } from '@shopflo/ui'
import { CaretDown, Gift, Percent, CurrencyInr, X } from '@phosphor-icons/react'
import { useDiscountForm } from '../../../hooks/useDiscountForm'
import { useEntitledProducts } from '../../../hooks/useEntitledProducts'
import {
  AmountType,
  DiscountApplyToType,
  BxgyEntitlement,
  DiscountFieldPaths,
  UiConfigDiscountType,
  EntitledCatalogueSelectionMode
} from 'src/lib/types/discountsV2'

import FormErrorText from 'src/components/common/FormErrorText'
import { AndBadge, BxgyBadge } from '../../../common/ProductIcons'
import DiscountInfoText from '../../../common/DiscountInfoText'
import CatalogueGroupControl from '../common/CatalogueGroupControl'
import { classNames } from 'src/lib/utils/helpers'
import { useLocale } from 'src/lib/hooks/useLocal'
import { discountCatalogueErrors } from 'src/lib/utils/catalogueErrorHelpers'

export enum BxgyDiscountType {
  FREE = 'FREE',
  PERCENTAGE = 'PERCENTAGE',
  AMOUNT = 'AMOUNT'
}

const allowedDiscountTypes = [BxgyDiscountType.FREE, BxgyDiscountType.PERCENTAGE, BxgyDiscountType.AMOUNT]

const DiscountApplicationRulesBxgyGetY = ({ type }: { type: UiConfigDiscountType }) => {
  const {
    state: { entitlement: rawEntitlement, errors, uiConfig },
    actions: { setFormState, registerRef }
  } = useDiscountForm()
  const { t } = useLocale()

  const entitlement = rawEntitlement as BxgyEntitlement
  const { metaData: entMetaData, entitledCatalogue, concessionAmount, amountType } = entitlement

  const [getYType, setGetYType] = useState<DiscountApplyToType>(
    entitlement?.applyTo || DiscountApplyToType.SPECIFIC_PRODUCTS
  )

  const catalogueType = getYType === DiscountApplyToType.SPECIFIC_COLLECTIONS ? 'COLLECTION' : 'PRODUCT_AND_VARIANT'
  const {
    handleItemsSelected,
    handleRemoveIncludedItem,
    handleRemoveExcludedItem,
    handleVariantUpdate,
    handleAddEmptyGroup,
    handleRemoveGroup,
    handleQuantityChange
  } = useEntitledProducts({
    entitlement,
    setFormState,
    catalogueType
  })

  const getYItems = entMetaData?.entitledCatalogue || []
  const displayGroups = getYItems.length > 0 ? getYItems : [[]]
  const hasEntitledProducts = entitlement?.entitledCatalogue?.some(group => group?.entities?.length > 0)

  const currentDiscountType = useMemo(() => {
    if (amountType === AmountType.PERCENTAGE && concessionAmount === 100) return BxgyDiscountType.FREE
    if (amountType === AmountType.PERCENTAGE) return BxgyDiscountType.PERCENTAGE
    return BxgyDiscountType.AMOUNT
  }, [amountType, concessionAmount])

  const handleApplyToTypeChange = (val: DiscountApplyToType) => {
    setGetYType(val)
    setFormState(
      {
        entitlement: {
          ...entitlement,
          applyTo: val,
          entitledCatalogue: [
            {
              quantity: 1,
              entities: [],
              selectionMode: EntitledCatalogueSelectionMode.ANY
            }
          ],
          excludedCatalogue: [],
          metaData: {
            ...entitlement.metaData,
            entitledCatalogue: [],
            excludedCatalogue: []
          }
        }
      },
      [DiscountFieldPaths.ENTITLED_CATALOGUE]
    )
  }

  const handleDiscountTypeChange = (type: BxgyDiscountType) => {
    let newAmountType = AmountType.PERCENTAGE
    let newConcession = 0

    if (type === BxgyDiscountType.FREE) {
      newAmountType = AmountType.PERCENTAGE
      newConcession = 100
    } else if (type === BxgyDiscountType.PERCENTAGE) {
      newAmountType = AmountType.PERCENTAGE
      newConcession = 0
    } else {
      newAmountType = AmountType.FLAT
      newConcession = 0
    }

    setFormState(
      {
        entitlement: {
          ...entitlement,
          amountType: newAmountType,
          concessionAmount: newConcession
        }
      },
      [DiscountFieldPaths.CONCESSION_AMOUNT]
    )
  }

  const handleConcessionAmountChange = (val: string) => {
    let newAmount = parseFloat(val) || 0
    if (amountType === AmountType.PERCENTAGE && newAmount > 100) {
      newAmount = 100
    }

    setFormState(
      {
        entitlement: {
          ...entitlement,
          concessionAmount: newAmount
        }
      },
      [DiscountFieldPaths.CONCESSION_AMOUNT]
    )
  }

  const getDiscountTypeIcon = () => {
    switch (currentDiscountType) {
      case BxgyDiscountType.FREE:
        return Gift
      case BxgyDiscountType.PERCENTAGE:
        return Percent
      case BxgyDiscountType.AMOUNT:
      default:
        return CurrencyInr
    }
  }

  const renderSelectedItems = () => {
    return (
      <div className='flex flex-col '>
        {displayGroups.map((group, groupIndex) => {
          return (
            <div key={groupIndex}>
              <div className='rounded-xl flex flex-col'>
                <div className='flex justify-between  '>
                  {groupIndex > 0 && (
                    <div className='flex flex-col gap-3 py-2'>
                      <div className='flex items-center gap-2 flex-wrap'>
                        <div className='flex items-center gap-2'>
                          <AndBadge />
                          <span className='text-text-sm-semibold text-zinc-800'>{t('discounts_v2.gets')}</span>
                        </div>

                        <div
                          className='flex items-center gap-2 min-w-10'
                          ref={node =>
                            registerRef(`${DiscountFieldPaths.ENTITLED_CATALOGUE}.${groupIndex}.quantity`, node)
                          }
                        >
                          <Input
                            id={`get-y-quantity-input-group-${groupIndex}`}
                            type='number'
                            value={entitledCatalogue[groupIndex]?.quantity || ''}
                            onChange={e => handleQuantityChange(groupIndex, e.target.value)}
                            onFocus={e => e.target.select()}
                            dimension='sm'
                            variant='filled'
                            style={{
                              width: `${(String(entitledCatalogue[groupIndex]?.quantity || '').length || 1) + 2}ch`
                            }}
                            className='!min-w-10 text-center'
                            error={!!errors[`${DiscountFieldPaths.ENTITLED_CATALOGUE}.${groupIndex}.quantity`]}
                          />
                          <span className='text-text-sm-semibold text-zinc-500'>{t('discounts_v2.quantity_of')}</span>
                        </div>
                      </div>

                      <FormErrorText
                        message={errors[`${DiscountFieldPaths.ENTITLED_CATALOGUE}.${groupIndex}.quantity`]}
                      />
                    </div>
                  )}

                  {groupIndex > 0 && (
                    <div className='flex items-center gap-2'>
                      <span className='text-text-xs-medium text-zinc-500'>
                        {t('discounts_v2.group_count', { count: groupIndex + 1 })}
                      </span>
                      <IconButton
                        onClick={() => handleRemoveGroup(groupIndex)}
                        variant='ghost'
                        size='xs'
                        IconComponent={X}
                        className='text-zinc-500'
                      />
                    </div>
                  )}
                </div>

                <div className='flex w-full mb-3'>
                  <div className='border-l-1 border-zinc-200 w-5 rounded-l-lg border-y-1 h-auto'></div>
                  <div className='flex flex-col rounded-lg px-2 w-full'>
                    <CatalogueGroupControl
                      groupIndex={groupIndex}
                      includedGroups={getYItems}
                      excludedGroups={entMetaData?.excludedCatalogue}
                      catalogueType={catalogueType}
                      actions={{
                        onRemoveIncludedItem: handleRemoveIncludedItem,
                        onRemoveExcludedItem: handleRemoveExcludedItem,
                        onUpdateItem: handleVariantUpdate,
                        onItemsSelected: handleItemsSelected,
                        onAddEmptyGroup: handleAddEmptyGroup
                      }}
                      errorHandling={{
                        errors: discountCatalogueErrors(errors, DiscountFieldPaths.ENTITLED_CATALOGUE)
                      }}
                      config={{
                        showVariantEditor: true,
                        addText: t('discounts_v2.add_product_label'),
                        showExclusionOption: true,
                        emptyText:
                          getYType === DiscountApplyToType.SPECIFIC_COLLECTIONS
                            ? t('discounts_v2.choose_collections')
                            : t('discounts_v2.choose_products')
                      }}
                    />
                  </div>
                  <div className='border-r-1 border-zinc-200 w-5 rounded-r-lg border-y-1 h-auto'></div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex items-center gap-2'>
        <BxgyBadge label='Y' />
        <div className='text-text-sm-semibold text-zinc-800'>{t('discounts_v2.then_gets')}</div>
        <div className='flex flex-col'>
          <div
            className='flex items-center gap-2 min-w-10'
            ref={node => registerRef(`${DiscountFieldPaths.ENTITLED_CATALOGUE}.0.quantity`, node)}
          >
            <Input
              id='get-y-quantity-input-0'
              type='number'
              value={entitledCatalogue[0]?.quantity || 1}
              onChange={e => handleQuantityChange(0, e.target.value)}
              onFocus={e => e.target.select()}
              className='text-center min-w-10'
              dimension='sm'
              variant='filled'
              style={{
                width: `${(String(entitledCatalogue[0]?.quantity || '').length || 1) + 2}ch`
              }}
              error={!!errors[`${DiscountFieldPaths.ENTITLED_CATALOGUE}.0.quantity`]}
            />
            <span className='text-zinc-500 text-text-sm-regular'>{t('discounts_v2.quantity_of')}</span>
          </div>
          <FormErrorText message={errors[`${DiscountFieldPaths.ENTITLED_CATALOGUE}.0.quantity`]} />
        </div>
      </div>

      <div className='flex gap-2 w-full'>
        <div className='flex-1'>
          <Select value={getYType} onValueChange={v => handleApplyToTypeChange(v as DiscountApplyToType)}>
            <SelectTrigger id='get-y-apply-to-trigger' className='w-full bg-white rounded-lg h-9'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={DiscountApplyToType.SPECIFIC_PRODUCTS}>
                {t('discounts_v2.specific_products')}
              </SelectItem>
              <SelectItem value={DiscountApplyToType.SPECIFIC_COLLECTIONS}>
                {t('discounts_v2.specific_collections')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className='flex items-center h-9 justify-center'>
          <span className='text-sm text-zinc-500'>{t('discounts_v2.for_label')}</span>
        </div>

        <div className='flex-1'>
          <div className='flex gap-2'>
            <div className='flex-1 flex gap-2'>
              <div className='flex-1'>
                <div
                  className='flex flex-col w-full'
                  ref={node => registerRef(DiscountFieldPaths.CONCESSION_AMOUNT, node)}
                >
                  <Input
                    id='get-y-concession-amount-input'
                    type='number'
                    value={currentDiscountType === BxgyDiscountType.FREE ? '' : concessionAmount}
                    onChange={e => handleConcessionAmountChange(e.target.value)}
                    onFocus={e => e.target.select()}
                    placeholder={currentDiscountType === BxgyDiscountType.FREE ? t('discounts_v2.free_label') : '0'}
                    dimension='md'
                    error={!!errors[DiscountFieldPaths.CONCESSION_AMOUNT]}
                    readOnly={currentDiscountType === BxgyDiscountType.FREE}
                    startAdornment={
                      <div className='w-fit'>
                        <Select
                          value={currentDiscountType}
                          onValueChange={value => handleDiscountTypeChange(value as BxgyDiscountType)}
                        >
                          <SelectTrigger
                            id='get-y-at-discount-trigger'
                            showCaret={false}
                            className='ml-[-5px] h-full gap-1.5 rounded-md px-1 py-1 border-1 border-zinc-950/10 bg-white hover:bg-zinc-500/10 shadow-sm focus:ring-0 focus:border-zinc-950/10'
                          >
                            <div className='flex items-center gap-1.5 pr-1.5 text-zinc-500'>
                              <Icon
                                IconComponent={getDiscountTypeIcon()}
                                size='sm'
                                className='text-zinc-500'
                              />
                              <Icon
                                IconComponent={CaretDown}
                                size='xxxs'
                                iconProps={{ weight: 'fill' }}
                                className='text-zinc-500'
                              />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            {allowedDiscountTypes.includes(BxgyDiscountType.FREE) && (
                              <SelectItem value={BxgyDiscountType.FREE}>
                                <div className='flex items-center gap-2'>
                                  <Icon IconComponent={Gift} size='xs' className='text-zinc-500' />
                                  <span>{t('discounts_v2.free_label')}</span>
                                </div>
                              </SelectItem>
                            )}
                            {allowedDiscountTypes.includes(BxgyDiscountType.PERCENTAGE) && (
                              <SelectItem value={BxgyDiscountType.PERCENTAGE}>
                                <div className='flex items-center gap-2'>
                                  <Icon IconComponent={Percent} size='xs' className='text-zinc-500' />
                                  <span>{t('discounts_v2.percentage_label')}</span>
                                </div>
                              </SelectItem>
                            )}
                            {allowedDiscountTypes.includes(BxgyDiscountType.AMOUNT) && (
                              <SelectItem value={BxgyDiscountType.AMOUNT}>
                                <div className='flex items-center gap-2'>
                                  <Icon IconComponent={CurrencyInr} size='xs' className='text-zinc-500' />
                                  <span>{t('discounts_v2.amount_off_label')}</span>
                                </div>
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    }
                    className='bg-white'
                  />
                  <FormErrorText message={errors[DiscountFieldPaths.CONCESSION_AMOUNT]} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DiscountInfoText
        text={
          type === UiConfigDiscountType.BXGY_AUTO_ADDED
            ? t('discounts_v2.bxgy_auto_added_info')
            : t('discounts_v2.bxgy_info')
        }
        show={hasEntitledProducts}
      />

      {/* Selected List - Get Y */}
      <div className='flex w-full mb-3'>
        <div className='flex flex-col gap-2 rounded-lg  w-full'>
          {renderSelectedItems()}
          <FormErrorText message={errors[DiscountFieldPaths.ENTITLED_CATALOGUE]} />
        </div>
      </div>
    </div>
  )
}

export default DiscountApplicationRulesBxgyGetY
